import { useEffect, useState } from "react";
import type { DocumentLook } from "@/lib/api";
import {
  paperOutline, placeLabel, sectionOf, type PaperBlock, type PaperOutline,
} from "./paperOutline";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * What the insert sheet and the Used in popover need to know about a paper:
 * its outline, and the numbers its last compile gave its tables.
 *
 * Two reads: the paper (with its blocks) and its reference index, which carries
 * the numbers kept from the last compile (`LabelNumbers`) without compiling.
 * Cached per paper for the page's life, and forgotten after a table goes in.
 */
export interface Paper {
  id: string;
  title: string;
  /** "article", "revtex4-2"… */
  docClass: string | null;
  columns: number;
  blocks: PaperBlock[];
  outline: PaperOutline;
  /** True when the numbers below come from a compile. */
  numbered: boolean;
  /** Block id → the number its label printed. */
  numbers: Record<string, string | null>;
}

interface DocBody {
  id: string;
  title?: string;
  latexDocumentClass?: string | null;
  documentCategory?: string | null;
  columns?: number;
  blocks?: PaperBlock[];
}
interface RefBody {
  numbered?: boolean;
  targets?: { blockId: string; number?: string | null }[];
}

const cache = new Map<string, Promise<Paper | null>>();

export function forgetPaper(id: string): void {
  cache.delete(id);
}

export function loadPaper(fetchWithAuth: Fetch, id: string): Promise<Paper | null> {
  const hit = cache.get(id);
  if (hit) return hit;
  const p = (async () => {
    try {
      const [docRes, refRes] = await Promise.all([
        fetchWithAuth(`/api/documents/${id}`),
        fetchWithAuth(`/api/documents/${id}/references`).catch(() => null),
      ]);
      if (!docRes.ok) return null;
      const doc = (await docRes.json()) as DocBody;
      const refs = refRes?.ok ? ((await refRes.json().catch(() => ({}))) as RefBody) : {};
      const blocks = doc.blocks ?? [];
      const numbers: Record<string, string | null> = {};
      for (const t of refs.targets ?? []) numbers[t.blockId] = t.number ?? null;
      return {
        id,
        title: doc.title || "Untitled",
        docClass: doc.latexDocumentClass || doc.documentCategory || null,
        columns: doc.columns ?? 1,
        blocks,
        outline: paperOutline(blocks),
        numbered: !!refs.numbered,
        numbers,
      };
    } catch {
      return null;
    }
  })();
  cache.set(id, p);
  // A failure is not remembered: the next look asks again.
  void p.then((v) => { if (!v) cache.delete(id); });
  return p;
}

export function usePaper(fetchWithAuth: Fetch, id: string | null | undefined): Paper | null | undefined {
  const [paper, setPaper] = useState<Paper | null | undefined>(undefined);
  useEffect(() => {
    if (!id) { setPaper(undefined); return; }
    let live = true;
    setPaper(undefined);
    void loadPaper(fetchWithAuth, id).then((p) => { if (live) setPaper(p); });
    return () => { live = false; };
  }, [fetchWithAuth, id]);
  return paper;
}

export interface BlockPlace {
  /** "§4 Results · Table 3", or the section alone before a compile. */
  where: string;
  /** "§4, Table 3": the same, for a line that is already busy. */
  short: string;
  number: string | null;
  /** The top-level section it sits in (the chapter a section chip names), if any. */
  chapter?: { id: string; title: string } | null;
}

const NOWHERE: BlockPlace = { where: "", short: "", number: null, chapter: null };

/** The top-level heading a block sits under, or null before the first one. */
export function chapterOf(paper: Pick<Paper, "blocks" | "outline">, blockId: string): { id: string; title: string } | null {
  const tops = new Map(paper.outline.sections.map((s) => [s.id, s]));
  let current: { id: string; title: string } | null = null;
  for (const b of [...paper.blocks].sort((a, c) => a.sortOrder - c.sortOrder)) {
    const top = tops.get(b.id);
    if (top) current = { id: top.id, title: top.title };
    if (b.id === blockId) return current;
  }
  return null;
}

/** Where a block sits in a paper. */
export function blockPlace(paper: Paper, blockId: string | null | undefined): BlockPlace {
  if (!blockId) return NOWHERE;
  const number = paper.numbered ? paper.numbers[blockId] ?? null : null;
  const section = sectionOf(paper.blocks, blockId, paper.outline);
  const short = [section ? (section.number ? `§${section.number}` : section.title) : "", number ? `Table ${number}` : ""]
    .filter(Boolean).join(", ");
  return { where: placeLabel(section, number), short, number, chapter: chapterOf(paper, blockId) };
}

// ── where a table is used ──────────────────────────────────────────────

export interface TableLink {
  documentId: string;
  documentTitle: string;
  blockId: string | null;
  /** That paper's look, or null for Classic (document themes, 2a–2c). */
  look?: DocumentLook | null;
  /** The top numbered heading the table sits under: "4", or "A" after an appendix. */
  sectionNumber?: string | null;
  /** That chapter's colour (`#RRGGBB`), only when the paper uses Index. */
  sectionColour?: string | null;
}

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** One `visible` entry as the client trusts it: unknown fields are dropped to null. */
export function readLink(raw: Partial<TableLink> & { documentId: string }): TableLink {
  return {
    documentId: raw.documentId,
    documentTitle: raw.documentTitle ?? "",
    blockId: raw.blockId ?? null,
    look: raw.look && typeof raw.look === "object" && raw.look.theme ? raw.look : null,
    sectionNumber: typeof raw.sectionNumber === "string" && raw.sectionNumber.trim() ? raw.sectionNumber.trim() : null,
    sectionColour: typeof raw.sectionColour === "string" && HEX.test(raw.sectionColour) ? raw.sectionColour : null,
  };
}

export interface TableUsage {
  /** Every paper using it, including ones this author cannot see. */
  total: number;
  visible: TableLink[];
  /** Tables made as a copy of this one, when the API says. */
  copies: number;
}

export async function loadUsage(fetchWithAuth: Fetch, tableId: string): Promise<TableUsage | null> {
  try {
    const res = await fetchWithAuth(`/api/tables/${tableId}/documents`);
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<TableUsage>;
    const visible = (body.visible ?? []).filter((l) => l && typeof l.documentId === "string").map(readLink);
    return { total: body.total ?? 0, visible, copies: body.copies ?? 0 };
  } catch {
    return null;
  }
}

/** `GET /api/tables/{id}/documents`, reloaded when `version` changes. */
export function useTableUsage(fetchWithAuth: Fetch, tableId: string | null | undefined, version = 0) {
  const [usage, setUsage] = useState<TableUsage | null | undefined>(undefined);
  useEffect(() => {
    if (!tableId) { setUsage(undefined); return; }
    let live = true;
    void loadUsage(fetchWithAuth, tableId).then((u) => { if (live) setUsage(u); });
    return () => { live = false; };
  }, [fetchWithAuth, tableId, version]);
  return usage;
}

/** Each visible link with where it sits: "§4 Results · Table 3". */
export function useLinkPlaces(fetchWithAuth: Fetch, links: TableLink[] | undefined) {
  const [places, setPlaces] = useState<Record<string, BlockPlace>>({});
  const key = (links ?? []).map((l) => `${l.documentId}:${l.blockId}`).join(",");
  useEffect(() => {
    let live = true;
    void Promise.all(
      (links ?? []).map(async (l) => {
        const paper = await loadPaper(fetchWithAuth, l.documentId);
        return [l.documentId, paper ? blockPlace(paper, l.blockId) : NOWHERE] as const;
      }),
    ).then((rows) => { if (live) setPlaces(Object.fromEntries(rows)); });
    return () => { live = false; };
    // `key` stands for `links`, whose identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWithAuth, key]);
  return places;
}
